/**
 * Вход (тикет T18b).
 *
 * Пространство фирмы спрашивается вместе с почтой и паролем: одна и та же почта может
 * работать в двух фирмах, и угадывать за человека, в какую он идёт, нельзя. Названо
 * Пространство прямо в адресе — поле не показываем, оно уже отвечено.
 *
 * Отказ во входе не уточняет, что именно не подошло. Ответ «такой почты здесь нет»
 * рассказал бы постороннему, кто в фирме работает, — а это ровно то, что мы не отдаём
 * (§ 2): невидимое и несуществующее отвечают одинаково.
 */
import { useState } from 'react';
import type { FormEvent, ReactNode } from 'react';
import { Button, Field, Notice } from '../ui/controls.js';
import { useT } from '../state/settings.js';
import { createApi } from '../net/api.js';

export function SignIn({
  spaceSlug,
  onSignedIn,
}: {
  readonly spaceSlug: string | null;
  readonly onSignedIn: (spaceSlug: string) => void;
}): ReactNode {
  const t = useT();
  const [space, setSpace] = useState(spaceSlug ?? '');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [working, setWorking] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    const slug = space.trim();
    if (!slug || !email.trim() || !password) {
      setRefusal(t('signIn.needAll'));
      return;
    }
    setWorking(true);
    setRefusal(null);
    try {
      await createApi(slug).signIn({ email: email.trim(), password });
      onSignedIn(slug);
    } catch {
      // Что именно не подошло — не уточняем нарочно (см. заголовок файла).
      setRefusal(t('signIn.failed'));
    } finally {
      setWorking(false);
    }
  };

  return (
    <main id="main" className="mx-auto flex min-h-dvh max-w-md flex-col justify-center gap-6 p-6">
      <h1 className="text-2xl font-semibold">{t('app.name')}</h1>

      <form onSubmit={submit} className="flex flex-col gap-4">
        <h2 className="text-lg">{t('signIn.title')}</h2>

        {spaceSlug ? null : (
          <Field
            label={t('signIn.space')}
            hint={t('signIn.spaceHint')}
            value={space}
            autoComplete="organization"
            onChange={(event) => setSpace(event.currentTarget.value)}
          />
        )}

        <Field
          label={t('signIn.email')}
          type="email"
          value={email}
          autoComplete="username"
          onChange={(event) => setEmail(event.currentTarget.value)}
        />

        <Field
          label={t('signIn.password')}
          type="password"
          value={password}
          autoComplete="current-password"
          onChange={(event) => setPassword(event.currentTarget.value)}
        />

        {refusal ? <Notice kind="error">{refusal}</Notice> : null}

        <Button
          type="submit"
          look="primary"
          disabled={working}
          className="justify-center py-2 text-base"
        >
          {working ? t('signIn.working') : t('signIn.submit')}
        </Button>
      </form>
    </main>
  );
}
